import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { injectIntl } from 'react-intl';

import GameContainer from './GameContainer.js';

import PenaltyCanvas from '../games/PenaltyCanvas.js';
import { useCanvasMachine } from '../../ts/games/canvasStateMachine.ts';
import { getSoundPropsById, i18nIntl, isEmpty, isNotEmpty } from '../../lib/ep.js';
import { clientFinalPage } from '../games/StageFinalMessages.js';
import { localizeFields } from '../builder/DynamicFields.js';

const Instructions = (props) => {
	return null;
};

const GoalsCounter = forwardRef(({ min_goals, ...props }, ref) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const num_initial_goals = 0;
	const [ num_goals, setNumGoals ] = useState(num_initial_goals);

	useImperativeHandle(ref, () => ({
		updateNumGoals(num) {
			setNumGoals(num);
		},

		reset() {
			setNumGoals(num_initial_goals);
		},
	}), []);

	return (
		<div class="gui_total_points header_block">
			<strong class="label">{i18n("Goals")}</strong>

			<strong>{num_goals}/{min_goals}</strong>
		</div>
	)
});

const PenaltyClient = ({ stage, ...props }) => {
	const mode = stage.Stage?.penalty_mode ?? 'real';
	
	const refs = useRef({});

	const canvas_images = () => {
		const images = [];

		if(isNotEmpty(stage.Stage, 'penalty_ball_image')) {
			images.push({
				id:'penalty_ball_image',
				name:stage.Stage.penalty_ball_image,
				url:stage.Stage.full_penalty_ball_image,
				fit:'contain'
			})
		}

		if(isNotEmpty(stage.Stage, 'penalty_left_logo')) {
			images.push({
				id:'penalty_left_logo',
				name:stage.Stage.penalty_left_logo,
				url:stage.Stage.full_penalty_left_logo,
				fit:'contain'
			})
		}

		if(isNotEmpty(stage.Stage, 'penalty_right_logo')) {
			images.push({
				id:'penalty_right_logo',
				name:stage.Stage.penalty_right_logo,
				url:stage.Stage.full_penalty_right_logo,
				fit:'contain'
			})
		}

		if(isNotEmpty(stage.Stage, 'penalty_shirt_barrier_image')) {
			images.push({
				id:'penalty_shirt_barrier_image',
				name:stage.Stage.penalty_shirt_barrier_image,
				url:stage.Stage.full_penalty_shirt_barrier_image,
				fit:'contain'
			})
		}
		
		return images;
	};

	const sound_fx = {
		kick:new Howl({ ...getSoundPropsById((mode == 'foosball') ? 'foosball-hit-and-roll' : 'football-kick') }),
		goal:new Howl({ ...getSoundPropsById((mode == 'foosball') ? 'foosball-goal' : 'football-goal') }),
		reject:new Howl({ ...getSoundPropsById((mode == 'foosball') ? 'foosball-reject' : 'football-kick') }),
		reject_player:new Howl({ ...getSoundPropsById((mode == 'foosball') ? 'foosball-reject-player' : 'football-uy') }),
		success:new Howl({ ...getSoundPropsById('applause') }),
		fail:new Howl({ ...getSoundPropsById('fail') }),
	};

	if(mode != 'foosball' && (stage.Stage?.penalty_sound_morse ?? true)) {
		sound_fx.extra_goal = new Howl({ ...getSoundPropsById('football-goal-morse') });
	}

	const [ state, dispatch ] = useCanvasMachine({
		images:canvas_images,
		images_dependencies:[
			stage.Stage.penalty_left_logo,
			stage.Stage.penalty_right_logo,
			stage.Stage.penalty_shirt_barrier_image,
		],
	});

	const onReset = () => {
		refs.current['goals_counter'].reset();
	};

	const final_stage = clientFinalPage(stage, props.actual_state);

	// we force the soundtrack in these two games if not set by the administrator:
	if(isEmpty(final_stage.Stage, 'game_soundtrack')) {
		final_stage.Stage.name_soundtrack = (mode == 'real') ? 'football-soundtrack.mp3' : 'foosball-soundtrack.mp3';
		final_stage.Stage.full_game_soundtrack = `${window.cdn_url}sounds/${final_stage.Stage.name_soundtrack}`;
	}
	final_stage.Stage.penalty_reaction_messages = localizeFields(final_stage.Stage.penalty_reaction_messages,props.locale)

	return (
		<GameContainer
			{...props}
			key={`penalty_${mode}`}
			stage={final_stage}
			onReset={onReset}
			custom_block={<GoalsCounter
				ref={(node) => { refs.current['goals_counter'] = node; }}
				min_goals={parseInt(stage.Stage?.penalty_num_goals ?? '1')}
				intl={props.intl}
			/>}
			instructions={<Instructions
				stage={stage}
				intl={props.intl}
			/>}
			game_type="penalty"
			display_audio_controls={true}
			auto_start_timer={false}
			instructions_has_border={false}
			instructions_classes='gui_flex_align_center'
			force_game_to_square={false}
			sound_fx={sound_fx}
		>
			<PenaltyCanvas
				indexed_images={state.images}
				updateActualGoals={(num) => refs.current['goals_counter'].updateNumGoals(num)}
			/>
		</GameContainer>
	)
};

export default injectIntl(PenaltyClient);